import React from "react";
import Link from "next/link";
import styles from "./SitemapPagination.module.scss";

// =============================================================================
// SITEMAP PAGINATION — crawler-first, no JavaScript required
// =============================================================================
//
// Deliberately NOT `components/us-website/Pagination`, which is wrong here in two
// independent ways:
//   1. Its onClick calls preventDefault() then onPageChange() — it is a
//      client-side refetch control that only looks like a link. Both of its
//      consumers (ProductRange, FNBProductRange) depend on that behaviour, so a
//      no-op onPageChange would leave preventDefault swallowing every click and
//      produce a pagination bar that does nothing without JS.
//   2. Its ellipsis algorithm renders "1 2 3 … 8" from page 1, putting the middle
//      pages two hops from the hub. This page exists to make every URL one hop
//      away, so every page number is rendered instead.
//
// Fixing either upstream would change behaviour on two live product-listing
// pages, so this is a separate component by design.
// =============================================================================

// Above this, rendering every number stops being reasonable and we fall back to
// a windowed view. ~1000 products at 250/page is 4 pages, so this is headroom
// for a 10x catalogue rather than an expected path.
const MAX_FULL_LIST = 40;

const getWindowedPages = (currentPage, totalPages) => {
  const pages = new Set([1, totalPages]);
  for (let i = currentPage - 2; i <= currentPage + 2; i += 1) {
    if (i > 1 && i < totalPages) pages.add(i);
  }
  return [...pages].sort((a, b) => a - b);
};

const SitemapPagination = ({ currentPage, totalPages, basePath }) => {
  if (!totalPages || totalPages <= 1) return null;

  const pages =
    totalPages <= MAX_FULL_LIST
      ? Array.from({ length: totalPages }, (_, i) => i + 1)
      : getWindowedPages(currentPage, totalPages);

  return (
    <nav className={styles.pagination} aria-label="Sitemap pages">
      {currentPage > 1 && (
        <Link
          href={`${basePath}/${currentPage - 1}`}
          rel="prev"
          className={styles.step}
        >
          Previous
        </Link>
      )}

      <ul className={styles.pageList}>
        {pages.map((page, index) => {
          const previous = pages[index - 1];
          const gap = previous && page - previous > 1;

          if (page === currentPage) {
            return (
              <li key={page} className={styles.pageItem}>
                {gap && <span className={styles.gap}>…</span>}
                <span aria-current="page" className={styles.current}>
                  {page}
                </span>
              </li>
            );
          }

          return (
            <li key={page} className={styles.pageItem}>
              {gap && <span className={styles.gap}>…</span>}
              <Link href={`${basePath}/${page}`} className={styles.pageLink}>
                {page}
              </Link>
            </li>
          );
        })}
      </ul>

      {currentPage < totalPages && (
        <Link
          href={`${basePath}/${currentPage + 1}`}
          rel="next"
          className={styles.step}
        >
          Next
        </Link>
      )}
    </nav>
  );
};

export default SitemapPagination;
